/**
 * Расписание проекта: пять масштабов одних и тех же дат.
 *
 *   таймлайн — как идёт проект в целом;
 *   год      — в каких месяцах сосредоточена работа;
 *   месяц    — что в этом месяце;
 *   неделя   — что прямо сейчас;
 *   день     — один день целиком, со всеми подробностями.
 *
 * Данные одни, меняется только масштаб, поэтому это один блок с
 * переключателем, а не пять разных.
 *
 * По умолчанию открывается неделя: обзор нужен чаще всего для ответа на
 * вопрос «что делать сегодня», а не «как выглядит год». Выбранный масштаб
 * запоминается — человек, работающий с месяцем, откроет месяц и завтра.
 *
 * В календаре можно работать, а не только смотреть: щелчок по пустому дню
 * создаёт задачу с этим сроком, задачу можно перетащить на другой день.
 * Цвет задачи — цвет ответственного; выполненное приглушается.
 */

import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  CalendarDaysIcon,
  CalendarSearchIcon,
  CalendarRangeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  GanttChartIcon,
  PlusIcon,
  SquareIcon,
} from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { TimelineDto, TimelineItemDto, UserDto } from '@project-os/contracts';
import { isOverdue } from '@project-os/contracts';
import { ApiError } from '@/api/client';
import { useUpdateItem } from '@/api/items';
import { useDisplaySettings } from '@/app/display-settings';
import { UserAvatar } from '@/components/common/user-avatar';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { dayKey, formatDate, pluralRu } from '@/lib/format';
import { memberColorStyle } from '@/lib/member-color';
import { useLanded } from '@/lib/use-landed';
import { useLocalSetting } from '@/lib/use-local-setting';
import { useWheelStep } from '@/lib/use-wheel';
import { cn } from '@/lib/utils';
import { ItemTypeIcon } from '../items/item-icon';
import { ProjectTimeline } from './ProjectTimeline';
import { QuickTaskDialog } from './QuickTaskDialog';

type Mode = 'timeline' | 'year' | 'month' | 'week' | 'day';

const MODES: { value: Mode; label: string; icon: typeof CalendarDaysIcon }[] = [
  { value: 'timeline', label: 'Таймлайн', icon: GanttChartIcon },
  { value: 'year', label: 'Год', icon: CalendarRangeIcon },
  { value: 'month', label: 'Месяц', icon: CalendarDaysIcon },
  { value: 'week', label: 'Неделя', icon: CalendarDaysIcon },
  { value: 'day', label: 'День', icon: SquareIcon },
];

const WEEKDAY_NAMES = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

/** Начало недели с учётом настройки первого дня. */
function startOfWeek(date: Date, weekStartIndex: 0 | 1): Date {
  const result = new Date(date);
  const shift = (result.getDay() - weekStartIndex + 7) % 7;
  result.setDate(result.getDate() - shift);
  result.setHours(0, 0, 0, 0);
  return result;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** Суббота и воскресенье независимо от того, с какого дня начинается неделя. */
function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}

export function ScheduleCard({
  projectId,
  timeline,
  members,
  canEdit,
  onOpenItem,
  onOpenStage,
}: {
  projectId: string;
  timeline: TimelineDto;
  members: UserDto[];
  canEdit: boolean;
  onOpenItem: (itemId: string) => void;
  onOpenStage?: (stageId: string) => void;
}) {
  const { weekStartIndex } = useDisplaySettings();
  // Ключ с номером: он менялся, когда умолчанием стала неделя. Прежнее
  // значение «месяц» осталось бы лежать в браузере и выглядело бы как
  // умолчание, хотя было разовым выбором полугодовой давности.
  const [mode, setMode] = useLocalSetting<Mode>('schedule-mode-v2', 'week');

  /**
   * Колесо над календарём меняет масштаб.
   *
   * Порядок в MODES не случаен и здесь работает как шкала: от таймлайна к
   * дню — от общего к частному. Поворот к себе укрупняет, от себя — отдаляет,
   * как в любом просмотрщике карт.
   *
   * На краях шкалы жест не перехватывается: дошли до дня или до таймлайна —
   * и колесо снова прокручивает страницу. Иначе календарь посреди обзора
   * оказался бы ловушкой для курсора.
   */
  const scaleRef = useRef<HTMLDivElement>(null);
  const scaleIndex = MODES.findIndex((option) => option.value === mode);

  useWheelStep(
    scaleRef,
    (direction) => {
      const next = MODES[scaleIndex - direction];
      if (next) setMode(next.value);
    },
    (direction) => Boolean(MODES[scaleIndex - direction]),
  );
  const [anchor, setAnchor] = useState(() => new Date());
  const [creatingOn, setCreatingOn] = useState<string | null>(null);

  const [dragging, setDragging] = useState<TimelineItemDto | null>(null);
  const [overDay, setOverDay] = useState<string | null>(null);
  const update = useUpdateItem(projectId);

  const sensors = useSensors(
    // Мышь и тачпад: перенос начинается после смещения, иначе щелчок по
    // задаче превращался бы в перетаскивание.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    // Палец: задержка вместо расстояния — иначе прокрутка списка
    // превращалась бы в перенос.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  const byDay = useMemo(() => {
    const map = new Map<string, TimelineItemDto[]>();
    for (const item of timeline.items) {
      const list = map.get(item.date);
      if (list) list.push(item);
      else map.set(item.date, [item]);
    }
    return map;
  }, [timeline.items]);

  const stageEnds = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const stage of timeline.stages) {
      const list = map.get(stage.endDate);
      if (list) list.push(stage.name);
      else map.set(stage.endDate, [stage.name]);
    }
    return map;
  }, [timeline.stages]);

  const weekdays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => WEEKDAY_NAMES[(index + weekStartIndex) % 7]!),
    [weekStartIndex],
  );

  const days = useMemo(() => {
    if (mode === 'day') return [new Date(anchor)];

    if (mode === 'week') {
      const start = startOfWeek(anchor, weekStartIndex);
      return Array.from({ length: 7 }, (_, index) => {
        const date = new Date(start);
        date.setDate(start.getDate() + index);
        return date;
      });
    }

    // Месяц: шесть недель, с «хвостами» соседних месяцев.
    const start = startOfWeek(startOfMonth(anchor), weekStartIndex);
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return date;
    });
  }, [mode, anchor, weekStartIndex]);

  const shift = (direction: number) => {
    const next = new Date(anchor);
    if (mode === 'day') next.setDate(next.getDate() + direction);
    else if (mode === 'week') next.setDate(next.getDate() + direction * 7);
    else if (mode === 'year') next.setFullYear(next.getFullYear() + direction);
    else next.setMonth(next.getMonth() + direction);
    setAnchor(next);
  };

  const today = dayKey(new Date());

  const periodLabel = useMemo(() => {
    if (mode === 'day') {
      return new Intl.DateTimeFormat('ru-RU', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      }).format(anchor);
    }
    if (mode === 'week') {
      return `${formatDate(days[0]!)} — ${formatDate(days[6]!)}`;
    }
    if (mode === 'year') return String(anchor.getFullYear());
    return new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' }).format(anchor);
  }, [mode, anchor, days]);

  const { landedId, markLanded } = useLanded();

  /**
   * Подсказка удобного масштаба.
   *
   * Неделя — правильное умолчание: на этом горизонте планируют. Но у
   * небольшого проекта на текущей неделе может не быть ничего, и тогда
   * человек видит пустую сетку и решает, что календарь сломан.
   *
   * Поэтому система не переключает масштаб сама — она говорит, где работа
   * есть, и предлагает перейти. Решение остаётся за человеком: молчаливое
   * переключение сбивало бы с толку сильнее пустой сетки.
   */
  const suggestion = useMemo((): {
    text: string;
    action: string;
    mode: Mode;
    goTo: string;
  } | null => {
    if (mode === 'timeline' || mode === 'year') return null;

    const inWindow = days.some((date) => (byDay.get(dayKey(date))?.length ?? 0) > 0);
    if (inWindow) return null;

    const total = timeline.items.length;
    if (total === 0) return null;

    // Ближайший срок — от него и считается, что предложить.
    const dates = timeline.items.map((item) => item.date).sort();
    const nearest = dates.find((date) => date >= today) ?? dates[dates.length - 1];
    if (!nearest) return null;

    const sameMonth = nearest.slice(0, 7) === today.slice(0, 7);

    // Из недели — в неделю с ближайшим сроком, если он в этом же месяце:
    // масштаб менять незачем, достаточно перелистнуть.
    if (mode === 'week' && sameMonth) {
      return {
        text: `На этой неделе сроков нет, ближайший — ${formatDate(nearest)}.`,
        action: 'Перейти',
        mode: 'week',
        goTo: nearest,
      };
    }

    // Иначе показывается месяц, в котором работа есть.
    if (mode !== 'month') {
      return {
        text: `${mode === 'week' ? 'На этой неделе' : 'В этом окне'} сроков нет, ближайший — ${formatDate(nearest)}.`,
        action: 'Показать этот месяц',
        mode: 'month',
        goTo: nearest,
      };
    }

    return {
      text: `В этом месяце сроков нет, ближайший — ${formatDate(nearest)}.`,
      action: 'Перейти',
      mode: 'month',
      goTo: nearest,
    };
  }, [mode, days, byDay, timeline.items, today]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const item = event.active.data.current?.item as TimelineItemDto | undefined;
    const target = event.over?.id ? String(event.over.id) : null;

    setDragging(null);
    setOverDay(null);

    if (!item || !target || target === item.date) return;

    try {
      await update.mutateAsync({ itemId: item.id, dueDate: target });
      markLanded(item.id);
      toast.success(`«${item.title}» — ${formatDate(target)}`);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось изменить срок');
    }
  };

  return (
    <div className="space-y-2.5" ref={scaleRef}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg bg-muted/60 p-0.5">
          {MODES.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setMode(option.value)}
              aria-pressed={mode === option.value}
              className={cn(
                'press flex items-center gap-1.5 rounded-md px-2 py-1 text-xs',
                mode === option.value
                  ? 'bg-card font-medium text-foreground shadow-soft'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <option.icon className="size-3.5" />
              {option.label}
            </button>
          ))}
        </div>

        {mode !== 'timeline' ? (
          <>
            <span className="text-sm font-medium first-letter:uppercase">{periodLabel}</span>
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="icon"
              className="icon-nudge-back size-7"
              onClick={() => shift(-1)}
              aria-label="Назад"
            >
              <ChevronLeftIcon className="size-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              onClick={() => setAnchor(new Date())}
            >
              Сегодня
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="icon-nudge size-7"
              onClick={() => shift(1)}
              aria-label="Вперёд"
            >
              <ChevronRightIcon className="size-4" />
            </Button>
          </>
        ) : null}
      </div>

      {mode === 'timeline' ? (
        <ProjectTimeline
          projectId={projectId}
          data={timeline}
          canEdit={canEdit}
          onOpenItem={onOpenItem}
          onOpenStage={onOpenStage}
          height="compact"
        />
      ) : mode === 'year' ? (
        <YearGrid
          year={anchor.getFullYear()}
          byDay={byDay}
          stageEnds={stageEnds}
          onPickMonth={(month) => {
            setAnchor(new Date(anchor.getFullYear(), month, 1));
            setMode('month');
          }}
        />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={(event) =>
            setDragging((event.active.data.current?.item as TimelineItemDto) ?? null)
          }
          onDragOver={(event) => setOverDay(event.over?.id ? String(event.over.id) : null)}
          onDragEnd={(event) => void handleDragEnd(event)}
          onDragCancel={() => {
            setDragging(null);
            setOverDay(null);
          }}
        >
          {mode !== 'day' ? (
            <div className="mb-1 grid grid-cols-7 gap-1 text-2xs text-muted-foreground">
              {weekdays.map((weekday) => (
                <div key={weekday} className="px-1">
                  {weekday}
                </div>
              ))}
            </div>
          ) : null}

          {suggestion ? (
            <div className="mb-1.5 flex flex-wrap items-center gap-2 rounded-md border border-border/50 bg-muted/40 px-2.5 py-1.5 text-2xs">
              <CalendarSearchIcon className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1">{suggestion.text}</span>
              <button
                type="button"
                onClick={() => {
                  setMode(suggestion.mode);
                  // Дата, а не только масштаб: иначе человек увидит другую
                  // сетку, по-прежнему пустую.
                  setAnchor(new Date(`${suggestion.goTo}T12:00:00`));
                }}
                className="press shrink-0 rounded px-1.5 py-0.5 font-medium text-primary hover:bg-primary-soft"
              >
                {suggestion.action}
              </button>
            </div>
          ) : null}

          <div
            key={mode}
            className={cn(
              'grid gap-1 animate-in fade-in-0 zoom-in-95',
              mode === 'day' ? 'grid-cols-1' : 'grid-cols-7',
            )}
          >
            {days.map((date) => (
              <DayCell
                key={dayKey(date)}
                date={date}
                mode={mode}
                today={today}
                anchorMonth={anchor.getMonth()}
                items={byDay.get(dayKey(date)) ?? []}
                stages={stageEnds.get(dayKey(date)) ?? []}
                canEdit={canEdit}
                isOver={overDay === dayKey(date)}
                landedId={landedId}
                onOpenItem={onOpenItem}
                onCreate={() => setCreatingOn(dayKey(date))}
              />
            ))}
          </div>

          {/* При переносе видно, на какое число задача встанет: без этого
              человек целится вслепую. */}
          <DragOverlay dropAnimation={null}>
            {dragging ? (
              <div
                data-drag-overlay
                className="surface-card drag-ghost flex items-center gap-1.5 border border-border-strong px-2 py-1 text-2xs"
              >
                <UserAvatar user={dragging.owner} size="2xs" />
                <span className="max-w-48 truncate">{dragging.title}</span>
                {overDay ? (
                  <span className="font-medium text-primary tabular-nums">
                    {formatDate(overDay)}
                  </span>
                ) : null}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {canEdit && mode !== 'timeline' && mode !== 'year' ? (
        <p className="text-2xs leading-relaxed text-muted-foreground/80">
          Щелчок по свободному месту в дне создаёт задачу с этим сроком. Задачу можно перетащить на
          другой день. Цвет метки — цвет ответственного; красная рамка означает просрочку,
          приглушённое — выполненное.
        </p>
      ) : null}

      <QuickTaskDialog
        projectId={projectId}
        date={creatingOn}
        members={members}
        onClose={() => setCreatingOn(null)}
        onCreated={onOpenItem}
      />
    </div>
  );
}

/**
 * Год: двенадцать месяцев со счётчиком дел.
 *
 * Отдельные дни в годовом масштабе неразличимы, поэтому показывается не
 * сетка дней, а плотность работы по месяцам — этого достаточно, чтобы
 * увидеть, где сосредоточена нагрузка, и провалиться в нужный месяц.
 */
function YearGrid({
  year,
  byDay,
  stageEnds,
  onPickMonth,
}: {
  year: number;
  byDay: Map<string, TimelineItemDto[]>;
  stageEnds: Map<string, string[]>;
  onPickMonth: (month: number) => void;
}) {
  const months = useMemo(() => {
    const counts = Array.from({ length: 12 }, () => ({ items: 0, done: 0, stages: 0 }));

    for (const [day, list] of byDay) {
      if (!day.startsWith(String(year))) continue;
      const month = Number(day.slice(5, 7)) - 1;
      counts[month]!.items += list.length;
      counts[month]!.done += list.filter((item) => item.isDone).length;
    }
    for (const [day, list] of stageEnds) {
      if (!day.startsWith(String(year))) continue;
      counts[Number(day.slice(5, 7)) - 1]!.stages += list.length;
    }
    return counts;
  }, [year, byDay, stageEnds]);

  const max = Math.max(1, ...months.map((month) => month.items));
  const currentMonth = new Date().getFullYear() === year ? new Date().getMonth() : -1;

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
      {months.map((month, index) => (
        <button
          key={index}
          type="button"
          onClick={() => onPickMonth(index)}
          className={cn(
            'row-hover rounded-lg border p-2 text-left transition-colors',
            index === currentMonth ? 'border-primary/50' : 'border-border/50',
          )}
        >
          <span className="flex items-baseline justify-between gap-2">
            <span className="text-xs font-medium first-letter:uppercase">
              {new Intl.DateTimeFormat('ru-RU', { month: 'long' }).format(new Date(year, index, 1))}
            </span>
            <span className="text-2xs text-muted-foreground tabular-nums">{month.items || ''}</span>
          </span>

          {/* Полоса нагрузки: доля выполненного залита успехом. */}
          <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-muted">
            {month.items > 0 ? (
              <span
                className="block h-full bg-primary/50"
                style={{ width: `${(month.items / max) * 100}%` }}
              />
            ) : null}
          </span>

          <span className="mt-1 block text-2xs text-muted-foreground">
            {month.items === 0 && month.stages === 0
              ? '—'
              : [
                  month.items > 0 ? `${month.done} из ${month.items}` : null,
                  month.stages > 0 ? pluralRu(month.stages, ['этап', 'этапа', 'этапов']) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
          </span>
        </button>
      ))}
    </div>
  );
}

function DayCell({
  date,
  mode,
  today,
  anchorMonth,
  items,
  stages,
  canEdit,
  isOver,
  landedId,
  onOpenItem,
  onCreate,
}: {
  date: Date;
  mode: Mode;
  today: string;
  anchorMonth: number;
  items: TimelineItemDto[];
  stages: string[];
  canEdit: boolean;
  isOver: boolean;
  landedId: string | null;
  onOpenItem: (itemId: string) => void;
  onCreate: () => void;
}) {
  const key = dayKey(date);
  const { setNodeRef } = useDroppable({ id: key, disabled: !canEdit });

  const isToday = key === today;
  const otherMonth = mode === 'month' && date.getMonth() !== anchorMonth;
  const weekend = isWeekend(date);

  const limit = mode === 'day' ? 50 : mode === 'week' ? 8 : 3;

  return (
    <div
      ref={setNodeRef}
      data-day={key}
      className={cn(
        'drop-zone group/day flex flex-col rounded-lg border p-1',
        mode === 'day' ? 'min-h-64' : mode === 'week' ? 'min-h-20' : 'min-h-16',
        // Выходные приглушены: в них обычно не планируют, и глаз должен
        // отличать их без чтения дат.
        weekend ? 'border-border/30 bg-muted/20' : 'border-border/50 bg-muted/45',
        // Соседние месяцы уводятся заметно дальше: прежние 55% ещё читались
        // как часть текущего месяца, и глаз спотыкался о числа, которые к
        // нему не относятся.
        otherMonth && 'bg-transparent opacity-30 saturate-50',
        isToday && 'ring-1 ring-primary/50',
        isOver && 'bg-primary-soft ring-1 ring-primary/60',
      )}
    >
      <div className="mb-1 flex items-center gap-1 px-1">
        <span
          className={cn(
            'text-2xs tabular-nums',
            weekend ? 'text-muted-foreground/70' : 'text-muted-foreground',
            isToday && 'rounded bg-primary px-1.5 font-semibold text-primary-foreground',
          )}
        >
          {mode === 'day'
            ? new Intl.DateTimeFormat('ru-RU', { weekday: 'long', day: 'numeric' }).format(date)
            : date.getDate()}
        </span>

        <div className="flex-1" />

        {canEdit ? (
          <button
            type="button"
            onClick={onCreate}
            className="text-muted-foreground opacity-0 transition-opacity group-hover/day:opacity-100 hover:text-foreground focus-visible:opacity-100"
            aria-label={`Добавить задачу на ${formatDate(key)}`}
          >
            <PlusIcon className="size-3" />
          </button>
        ) : null}
      </div>

      {stages.map((name) => (
        <p
          key={name}
          className="mb-0.5 truncate rounded bg-primary-soft px-1 text-2xs text-primary"
          title={`Целевая дата этапа: ${name}`}
        >
          {name}
        </p>
      ))}

      <div className="space-y-0.5">
        {items.slice(0, limit).map((item) => (
          <DayItem
            key={item.id}
            item={item}
            canEdit={canEdit}
            onOpen={onOpenItem}
            landed={item.id === landedId}
          />
        ))}
      </div>

      {items.length > limit ? (
        <p className="px-1 text-2xs text-muted-foreground">ещё {items.length - limit}</p>
      ) : null}

      {/* Пустое место в дне — кнопка создания: щелчок по нему записывает
          задачу с этим сроком, не уводя со страницы. */}
      {canEdit ? (
        <button
          type="button"
          onClick={onCreate}
          className="min-h-3 flex-1 cursor-pointer rounded"
          aria-label={`Добавить задачу на ${formatDate(key)}`}
          tabIndex={-1}
        />
      ) : null}
    </div>
  );
}

function DayItem({
  item,
  canEdit,
  onOpen,
  landed,
}: {
  item: TimelineItemDto;
  canEdit: boolean;
  onOpen: (itemId: string) => void;
  landed: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: item.id,
    data: { item },
    disabled: !canEdit,
  });

  const overdue = !item.isDone && isOverdue(item.date);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          ref={setNodeRef}
          type="button"
          {...attributes}
          {...listeners}
          onClick={() => onOpen(item.id)}
          aria-label={`${item.title}${overdue ? ', просрочено' : ''}`}
          className={cn(
            'row-hover press flex w-full items-center gap-1 rounded px-1 py-0.5 text-left',
            canEdit && 'cursor-grab touch-none select-none active:cursor-grabbing',
            isDragging && 'drag-source',
            landed && 'drop-landed',
          )}
        >
          {/* Маленький круглый аватар: кто отвечает, видно не наводя курсор.
              Пропорции держит сам компонент — в клетке дня деформированный
              кружок заметнее всего. */}
          {/* Цвет — человек, срочность — рамка. То же правило, что на шкале
              и на доске: одинаковые метки не должны значить разное. */}
          <UserAvatar
            user={item.owner}
            size="2xs"
            className={cn(
              item.isDone && 'opacity-50 grayscale',
              overdue && 'ring-1 ring-tone-danger',
            )}
          />
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-2xs',
              item.isDone && 'text-muted-foreground line-through',
              overdue && 'text-tone-danger',
            )}
          >
            {item.title}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-64">
        <p className="flex items-center gap-1.5 font-medium">
          <ItemTypeIcon type={item.type} className="size-3" />
          {item.title}
        </p>
        <p className="text-2xs opacity-80">
          {item.typeLabelRu} · {item.statusLabelRu}
        </p>
        {item.owner ? (
          <p className="flex items-center gap-1.5 text-2xs opacity-80">
            <span
              className="member-color size-1.5 rounded-full"
              style={memberColorStyle(item.owner.id)}
              aria-hidden
            />
            {item.owner.name}
          </p>
        ) : (
          <p className="text-2xs opacity-80">без ответственного</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
